import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, MapPin, Clock, Users, Briefcase, MessageSquare, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

function LandingPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [demoLoading, setDemoLoading] = useState({ bartender: false, owner: false });
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender Jobs & Flexible Bar Shifts | BarsConnect',
      description:
        'Find bartender jobs, event gigs, and flexible bar shifts. Create your free bartender profile and get discovered by bars hiring now.',
      keywords:
        'bartender jobs, flexible bar shifts, event bartender gigs, bartender profile, bars hiring bartenders',
      canonicalUrl: 'https://barsconnect.com/',
      robots: 'index,follow',
      ogTitle: 'Bartender Jobs & Flexible Bar Shifts | BarsConnect',
      ogDescription:
        'Create your free bartender profile, find flexible bar shifts, and connect directly with bars hiring now.',
      twitterTitle: 'Bartender Jobs & Flexible Bar Shifts | BarsConnect',
      twitterDescription:
        'Find bartender jobs and event gigs fast. Join BarsConnect free and get discovered by bars hiring now.',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'BarsConnect',
        url: 'https://barsconnect.com',
        logo: 'https://barsconnect.com/favicon.ico',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'BarsConnect',
        url: 'https://barsconnect.com',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://barsconnect.com/bartenders?search={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'BarsConnect',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  const demoBartenderEmail = process.env.REACT_APP_DEMO_BARTENDER_EMAIL;
  const demoOwnerEmail = process.env.REACT_APP_DEMO_OWNER_EMAIL;
  const demoPassword = process.env.REACT_APP_DEMO_PASSWORD;

  const handleDemoLogin = async (userType) => {
    if (!demoBartenderEmail || !demoOwnerEmail || !demoPassword) {
      toast.error('Demo login is not configured for this environment.');
      return;
    }

    setDemoLoading(prev => ({ ...prev, [userType]: true }));
    
    try {
      // Demo credentials
      const demoCredentials = {
        bartender: { email: demoBartenderEmail, password: demoPassword },
        owner: { email: demoOwnerEmail, password: demoPassword }
      };

      const credentials = demoCredentials[userType];
      
      // Try to login first
      try {
        await axios.post('/auth/login', credentials);
        const loginResult = await login(credentials.email, credentials.password);
        if (!loginResult.success) {
          throw new Error(loginResult.error || 'Demo login setup failed');
        }

        toast.success(`Welcome to ${userType === 'bartender' ? 'Bartender' : 'Bar Owner'} Demo Mode!`);
        navigate('/dashboard');
        window.location.reload();
      } catch (loginError) {
        // If login fails, try to register
        if (loginError.response?.status === 401) {
          const registerData = {
            email: credentials.email,
            password: credentials.password,
            first_name: userType === 'bartender' ? 'Demo' : 'Demo',
            last_name: userType === 'bartender' ? 'Bartender' : 'Owner',
            user_type: userType === 'bartender' ? 'bartender' : 'bar_owner'
          };
          
          await axios.post('/auth/register', registerData);
          const registerLoginResult = await login(credentials.email, credentials.password);
          if (!registerLoginResult.success) {
            throw new Error(registerLoginResult.error || 'Demo account login failed after registration');
          }
          
          toast.success(`Demo account created! Exploring as ${userType === 'bartender' ? 'Bartender' : 'Bar Owner'}`);
          navigate('/dashboard');
          window.location.reload();
        } else {
          throw loginError;
        }
      }
    } catch (error) {
      console.error('Demo login failed:', error);
      toast.error('Failed to start demo. Please try again.');
    } finally {
      setDemoLoading(prev => ({ ...prev, [userType]: false }));
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1760598742492-7ad941e658e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBiYXIlMjBpbnRlcmlvciUyMG5pZ2h0fGVufDB8fHx8MTc2OTA2MjU5MHww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-background/80" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Free Beta Badge */}
            <div className="mb-6 inline-flex items-center">
              <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 text-sm font-medium animate-pulse">
                <Star className="h-4 w-4 mr-2" />
                FREE Beta Access — No credit card required
              </Badge>
            </div>
            
            <h1 
              className="text-5xl md:text-7xl font-bold tracking-tight uppercase text-white mb-6"
              data-testid="hero-title"
            >
              Find Flexible Bartender
              <br />
              <span className="text-primary">Shifts That Fit Your Schedule</span>
            </h1>
            <p className="text-xl md:text-2xl leading-relaxed text-muted-foreground mb-8 max-w-3xl mx-auto">
              Build your free bartender profile, get discovered by bars hiring now, and book event gigs faster.
            </p>
            
            {/* Main CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button 
                asChild 
                size="lg"
                data-testid="create-free-bartender-profile-cta-btn"
                className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] h-12 px-8 rounded-md font-bold tracking-wide transition-all shadow-[0_0_20px_-5px_rgba(255,45,138,0.5)] hover:shadow-[0_0_30px_-5px_rgba(255,45,138,0.7)]"
              >
                <Link to="/register/bartenders">Create Free Bartender Profile</Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg"
                data-testid="find-bartender-jobs-cta-btn"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80 h-12 px-8 rounded-md font-medium border border-white/10"
              >
                <Link to="/bartender-jobs">Find Bartender Jobs</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                data-testid="for-bar-owners-cta-btn"
                className="h-12 px-8 rounded-md font-medium border border-primary/40 text-primary hover:bg-primary/10"
              >
                <Link to="/for-bar-owners">For Bar Owners</Link>
              </Button>
            </div>

            <div className="mb-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="landing-role-login-links-wrapper">
              <Link to="/login" className="text-sm text-primary hover:underline" data-testid="bartender-login-text-link">
                Bartender Login
              </Link>
              <Link to="/login" className="text-sm text-primary hover:underline" data-testid="owner-login-text-link">
                Bar Owner Login
              </Link>
              <Link to="/register" className="text-sm text-muted-foreground hover:text-primary hover:underline" data-testid="owner-register-text-link">
                New Owner? Create Account
              </Link>
            </div>

            {/* Demo Preview Buttons */}
            <div className="flex flex-col items-center gap-3 mt-8">
              <p className="text-sm text-muted-foreground">Or try the platform instantly:</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleDemoLogin('bartender')}
                  disabled={demoLoading.bartender}
                  variant="outline"
                  size="sm"
                  className="border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50"
                >
                  {demoLoading.bartender ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-current mr-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Try Bartender Demo
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleDemoLogin('owner')}
                  disabled={demoLoading.owner}
                  variant="outline"
                  size="sm"
                  className="border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50"
                >
                  {demoLoading.owner ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-current mr-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Try Bar Owner Demo
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why BarsConnect */}
      <section className="py-20 md:py-24" data-testid="why-barsconnect-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white mb-4">
              Why Bartenders Choose BarsConnect
            </h2>
            <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              Flexible shifts, event gigs, and direct connections with bars — all from one free profile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {/* Flexible shifts */}
            <Card className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 p-6 hover:border-primary/30 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <CardContent className="p-0">
                <div 
                  className="w-full h-48 rounded-lg mb-6 bg-cover bg-center"
                  role="img"
                  aria-label="Bar owner reviewing bartender talent using BarsConnect hiring platform"
                  style={{
                    backgroundImage: 'url(/images/barsconnect-bar-owner-hiring-bartender.jpg)'
                  }}
                />
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                  Flexible Shifts
                </Badge>
                <h3 className="text-2xl md:text-3xl font-semibold tracking-normal text-white mb-3">
                  Pick the shifts that fit your life
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center">
                    <Users className="h-4 w-4 mr-2 text-primary" />
                    Full-time and part-time bartending roles
                  </li>
                  <li className="flex items-center">
                    <Star className="h-4 w-4 mr-2 text-primary" />
                    Evening and weekend opportunities
                  </li>
                  <li className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-primary" />
                    Local gigs in your area
                  </li>
                  <li className="flex items-center">
                    <MessageSquare className="h-4 w-4 mr-2 text-primary" />
                    Apply fast with your profile
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Event gigs */}
            <Card className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 p-6 hover:border-primary/30 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <CardContent className="p-0">
                <div 
                  className="w-full h-48 rounded-lg mb-6 bg-cover bg-center"
                  role="img"
                  aria-label="Professional bartender crafting cocktails behind the bar"
                  style={{
                    backgroundImage: 'url(/images/barsconnect-bartender-crafting-cocktail.jpg)'
                  }}
                />
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                  Event Gigs
                </Badge>
                <h3 className="text-2xl md:text-3xl font-semibold tracking-normal text-white mb-3">
                  Get booked for high-energy events
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center">
                    <Briefcase className="h-4 w-4 mr-2 text-primary" />
                    Weddings and private parties
                  </li>
                  <li className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-primary" />
                    Festivals and nightlife events
                  </li>
                  <li className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-primary" />
                    Pop-up and seasonal opportunities
                  </li>
                  <li className="flex items-center">
                    <Star className="h-4 w-4 mr-2 text-primary" />
                    Fast direct responses from bars
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Direct connections + free profile */}
            <Card className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 p-6 hover:border-primary/30 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <CardContent className="p-0">
                <div 
                  className="w-full h-48 rounded-lg mb-6 bg-cover bg-center"
                  role="img"
                  aria-label="Modern bar interior representing AI-powered bartender and bar owner matching"
                  style={{
                    backgroundImage: 'url(/images/barsconnect-ai-bar-matching.jpg)'
                  }}
                />
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                  Free Profile
                </Badge>
                <h3 className="text-2xl md:text-3xl font-semibold tracking-normal text-white mb-3">
                  Connect directly with bars
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center">
                    <Star className="h-4 w-4 mr-2 text-primary" />
                    One profile shared with many bars
                  </li>
                  <li className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-primary" />
                    Free to create and update
                  </li>
                  <li className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-primary" />
                    Showcase skills and experience
                  </li>
                  <li className="flex items-center">
                    <Briefcase className="h-4 w-4 mr-2 text-primary" />
                    Message bars hiring right now
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-24" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white mb-10 text-center">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: 'create', title: 'Create your free profile', copy: 'Add your experience, skills, and availability in minutes.' },
              { id: 'discover', title: 'Get discovered by bars', copy: 'Bars search local bartender profiles and reach out directly.' },
              { id: 'book', title: 'Book your next shift', copy: 'Message hiring bars and secure your next flexible gig faster.' },
            ].map((step) => (
              <Card key={step.id} className="bg-white/5 border border-white/10" data-testid={`how-it-works-${step.id}-card`}>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.copy}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24" data-testid="homepage-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white mb-8 text-center">Bartender FAQ</h2>
          <div className="space-y-4">
            {[
              { id: 'free', q: 'Is BarsConnect free for bartenders?', a: 'Yes. Creating and maintaining your bartender profile is free.' },
              { id: 'jobs', q: 'What kind of bartender jobs are available?', a: 'You can find flexible shifts, event gigs, and recurring bar roles.' },
              { id: 'speed', q: 'How fast can I start getting opportunities?', a: 'Many bartenders start receiving profile views and messages shortly after completing their profile.' },
            ].map((item) => (
              <Card key={item.id} className="bg-white/5 border border-white/10" data-testid={`homepage-faq-${item.id}`}>
                <CardContent className="p-5">
                  <h3 className="text-lg font-semibold text-white mb-1">{item.q}</h3>
                  <p className="text-sm text-muted-foreground">{item.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
            Ready to Get More Bartender Shifts?
          </h2>
          <p className="text-lg leading-relaxed text-muted-foreground mb-8 max-w-2xl mx-auto">
            Create your bartender profile now and connect directly with bars hiring in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg"
              data-testid="final-cta-create-bartender-profile-btn"
              className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] h-12 px-8 rounded-md font-bold uppercase tracking-wide transition-all shadow-[0_0_20px_-5px_rgba(255,45,138,0.5)] hover:shadow-[0_0_30px_-5px_rgba(255,45,138,0.7)]"
            >
              <Link to="/register/bartenders">Create Free Bartender Profile</Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg"
              data-testid="final-cta-find-jobs-btn"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80 h-12 px-8 rounded-md font-medium border border-white/10"
            >
              <Link to="/bartender-jobs">Find Bartender Jobs</Link>
            </Button>
          </div>

          <div className="mt-8 border-t border-white/10 pt-6" data-testid="owner-messaging-below-fold-section">
            <p className="text-sm text-muted-foreground mb-3">Hiring bartenders as a bar owner?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button asChild variant="outline" size="sm" data-testid="owner-below-fold-post-job-btn">
                <Link to="/for-bar-owners">For Bar Owners</Link>
              </Button>
              <Link to="/login" className="text-sm text-primary hover:underline" data-testid="owner-below-fold-login-link">
                Owner Login
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
